import React from 'react';
import { useFetchNaiveDevicesQuery, useCreateNaiveDeviceMutation } from 'entities/Connection';
import { Flex, QueryStatusBoundary, Typography } from 'shared/_ui';
import { useCreateXrayConnection } from 'features/CreateXrayConnection';

import {
  ConnectionAdd,
  useDeleteXrayConnection,
  useFetchUserInfo,
  useFetchXrayConnections,
} from 'entities/Connection';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { CONTROL_IDS } from 'shared/consts';
import { useStoreUiControls } from 'shared/lib';
import { useConnectionDevices } from 'entities/Device';
import { XrayConnectionCard } from 'entities/Connection';
import { XrayConnectionActions } from 'features/ConnectionActions';
import { XrayCountrySelect } from 'features/XrayCountrySelect';
import { notify } from 'shared';
import { getTariffType } from 'entities/Tariff';

const ConnectionList: React.FC = () => {
  const { t } = useTranslation();

  const {
    isLoading: isUserDataLoading,
    data: userData,
    isError: isUserDataError,
    refetch: refetchUserData,
  } = useFetchUserInfo(null);

  const { open, setData } = useStoreUiControls(CONTROL_IDS.GUIDE);
  const { data: legacyConnections } = useFetchXrayConnections(null, { skip: userData?.pricingRegion === 'RU' });
  const { data: devices, isLoading: isDevicesLoading, isError: isDevicesError, refetch: refetchDevices } = useFetchNaiveDevicesQuery(null, { skip: userData?.pricingRegion !== 'RU', pollingInterval: 15000 });
  const [createNaiveDevice, { isLoading: isCreatingDevice }] = useCreateNaiveDeviceMutation();
  const xrayConnections = userData?.pricingRegion === 'RU'
    ? devices?.filter((device) => device.status !== 'REVOKED').map((device) => ({
      id: device.id, name: device.name, expiredAt: device.expiredAt, lifecycle: device.status,
      protocol: 'NAIVE_PROXY', country: '', serverId: 0, subscription: '',
    })) : legacyConnections;
  const { getDevice } = useConnectionDevices();
  const { open: openModal } = useStoreUiControls(CONTROL_IDS.FREE_CONNECTION_MODAL);

  const isRu = userData?.pricingRegion === 'RU';
  const onConnectionAdd = async () => {
    if (isRu) {
      try {
        const created = await createNaiveDevice({}).unwrap();
        if (created.status === 'ACTIVE') {
          setData({ isConnectionCreate: false, connectionId: created.id });
          open();
        } else {
          notify.info(t('xray.devicePreparing'));
        }
      } catch {
        notify.error(t('xray.createError'));
      }
      return;
    }
    if (!userData || getTariffType(userData?.tariff) === "free") {
      openModal();
      return;
    }
    setData({ isConnectionCreate: true });
    open();
  };

  const isAddDisabled = userData ? (xrayConnections ? xrayConnections?.length : 0) >= userData.tariff.numberOfAccounts : false;

  const [createXrayConnection, { isLoading: isCreateXrayLoading }] =
    useCreateXrayConnection();

  const [deleteXrayConnection, { isLoading: isDeleteXrayConnectionLoading }] =
    useDeleteXrayConnection();

  return (
    <Flex direction="column" spacing={20}>
      <Typography.Title level={2}>{t('connection.list')}</Typography.Title>
      <Flex direction="column">
        <QueryStatusBoundary
          isLoading={isUserDataLoading || (isRu && isDevicesLoading)}
          isError={isUserDataError || (isRu && isDevicesError)}
          refresh={isRu && isDevicesError ? refetchDevices : refetchUserData}
        >
          {isRu && <span>{t('xray.deviceSlots', { used: xrayConnections?.length ?? 0, limit: userData?.tariff.numberOfAccounts ?? 0 })}</span>}
          <div className={styles.container}>
            {xrayConnections?.map((xrayConnection) => (
              <XrayConnectionCard
                expiredAt={xrayConnection.expiredAt}
                key={xrayConnection.id}
                statusText={xrayConnection.lifecycle && xrayConnection.lifecycle !== 'ACTIVE'
                  ? t(xrayConnection.lifecycle === 'REVOKE_PENDING'
                    ? 'xray.deviceDeleting' : 'xray.devicePreparing') : undefined}
                actionsMenu={<XrayConnectionActions id={xrayConnection.id} naive={xrayConnection.protocol === 'NAIVE_PROXY'}
                  setupAvailable={!xrayConnection.lifecycle || xrayConnection.lifecycle === 'ACTIVE'} />}
                countryMenu={
                  xrayConnection.protocol === 'NAIVE_PROXY' ? <span>{('name' in xrayConnection && xrayConnection.name) || `${t('xray.device')} #${xrayConnection.id}`} / PrivateGate / Multi-exit</span> : <XrayCountrySelect
                    isNewConnectionLoading={
                      isCreateXrayLoading || isDeleteXrayConnectionLoading
                    }
                    onConfirm={async (
                      connectionId: number,
                      countryId: number
                    ) => {
                      if (
                        xrayConnections.some(
                          (connection) => connection.serverId === countryId
                        )
                      ) {
                        notify.info(t('xray.duplicateConnection'));
                        return;
                      }
                      await deleteXrayConnection(connectionId);

                      createXrayConnection(countryId);
                    }}
                    country={xrayConnection.country}
                    id={xrayConnection.id}
                  />
                }
                host={xrayConnection.subscription}
                device={getDevice(xrayConnection.id) ?? 'windows'}
              />
            ))}
            {(!isRu || !isAddDisabled) && <ConnectionAdd
              disabled={isAddDisabled || isCreateXrayLoading || isCreatingDevice}
              onConnectionAdd={onConnectionAdd}
            />}
          </div>
        </QueryStatusBoundary>
      </Flex>
    </Flex>
  );
};

export { ConnectionList };
